import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import loader from "../../assets/images/loader.gif";
import { FaUser, FaEnvelope, FaPhoneAlt, FaPlaneDeparture, FaPlane, FaCreditCard } from "react-icons/fa";

import {
    setBookingId,
    setBookingInfoRecordId,
} from "../../features/bookingSlice/bookingSlice";
import { createBookingWithPayment } from "../../api/bookingApi.js";
import { registerPassenger, processSquarePayment } from "../../api/squareApi.js";
import { SQUARE_CONFIG } from "../../config/squareConfig";

function Payment() {
    const [loading, setLoading] = useState(false);
    const [countryCode, setCountryCode] = useState("+1");
    const [cardHolderName, setCardHolderName] = useState("");
    const [paymentError, setPaymentError] = useState("");
    const [squareCard, setSquareCard] = useState(null);

    const bookingState = useSelector((state) => state.booking);
    const { 
        fleet_record_with_rate_id, 
        maps_record_id, 
        quote_info_record_id, 
        selected_vehicle_id,
        booking_info_record_id,
        pickup,
        dropoff,
        date,
        time,
        fleet_selection
    } = bookingState;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Initialize Square payment form
    useEffect(() => {
        let isMounted = true;
        
        const initializeSquarePayment = async () => {
            try {
                // Clear any existing card elements first
                const cardContainer = document.getElementById('square-payment-card');
                if (cardContainer) {
                    cardContainer.innerHTML = '';
                }

                // Wait for Square SDK to load
                let attempts = 0;
                const maxAttempts = 20;
                
                while (!window.Square && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 250));
                    attempts++;
                }
                
                if (!window.Square) {
                    console.warn('Square Web Payments SDK not available');
                    return;
                }

                if (!isMounted) return; // Component unmounted during initialization

                const payments = window.Square.payments(
                    SQUARE_CONFIG.applicationId,
                    SQUARE_CONFIG.locationId
                );

                const cardElement = await payments.card();
                
                if (!isMounted) return; // Component unmounted during initialization
                
                await cardElement.attach('#square-payment-card');
                
                if (isMounted) {
                    setSquareCard(cardElement);
                    setPaymentError('');
                }
            } catch (error) {
                console.error('Square initialization failed:', error);
                if (isMounted) {
                    setPaymentError('Payment form initialization failed. Please refresh the page.');
                }
            }
        };

        initializeSquarePayment();

        // Cleanup function
        return () => {
            isMounted = false;
            if (squareCard) {
                try {
                    squareCard.destroy();
                } catch (error) {
                    console.warn('Error destroying Square card element:', error);
                }
            }
        };
    }, []);

    const handleConfirm = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData(e.target);
        const phone = `${countryCode}${formData.get("phone")}`;

        // DEBUG: Log form data values
        console.log('[Payment] Form data values:');
        console.log('- full-name:', formData.get("full-name"));
        console.log('- email:', formData.get("email"));
        console.log('- phone:', phone);
        console.log('- date from Redux:', date);
        console.log('- time from Redux:', time);

        try {
            // Validate card information first
            if (!squareCard) {
                setPaymentError('Payment form not ready. Please refresh the page.');
                setLoading(false);
                return;
            }

            if (!cardHolderName.trim()) {
                setPaymentError('Please enter the cardholder name.');
                setLoading(false);
                return;
            }

            // Clear any previous errors
            setPaymentError('');

            // First register passenger
            const passengerData = {
                name: formData.get("full-name"),
                email: formData.get("email"),
                phone: phone
            };

            const passengerResponse = await registerPassenger(passengerData);
            console.log("Passenger registration:", JSON.stringify(passengerResponse, null, 2));

            if (!passengerResponse.passenger_id) {
                throw new Error(passengerResponse.msg || passengerResponse.message || 'Passenger registration failed');
            }

            const passengerId = passengerResponse.passenger_id;

            // Create booking data - use Redux fleet_id instead of selected_vehicle_id
            const bookingData = {
                passenger_id: passengerId,
                start: pickup?.address || pickup?.name || '',
                end: dropoff?.address || dropoff?.name || '',
                journey_date: `${date} ${time}:00`,
                fleet_id: bookingState.fleet_id, // ✅ Use Redux fleet_id (the one user selected)
                payment_method: "Square Payment ($1)", // ✅ Updated to reflect $1 charge
                domain: window.location.hostname,
                fare_details: {
                    selected_fleet_id: bookingState.fleet_id, // ✅ Use Redux fleet_id
                    journey_fare: (fleet_selection?.fare_details?.journey_fare || fleet_selection?.fare_details?.total_fare || 0).toString(),
                    add_charges: fleet_selection?.fare_details?.add_charges || [],
                    other_add_charges: [],
                    discount: (fleet_selection?.fare_details?.discount || 0).toString(),
                    final_fare: (fleet_selection?.fare_details?.final_fare || fleet_selection?.fare_details?.total_fare || 0).toString()
                },
                flight_number: formData.get("flight-number") || '',
                flight_name: formData.get("flight-name") || '',
                special_requirements: '',
                // Add email and phone as required by backend
                customer_email: formData.get("email"),
                customer_cell_no: phone
            };

            console.log('[Payment] Step 2: Creating booking with improved booking flow');

            // Create booking with payment using the comprehensive booking API
            const confirmData = {
                passenger_id: passengerResponse.passenger_id,
                start: bookingData.start,
                end: bookingData.end,
                pickup: bookingState.pickup,
                dropoff: bookingState.dropoff,
                journey_date: bookingData.journey_date,
                fleet_id: bookingData.fleet_id,
                amount: "1.00", // ✅ Charge $1 instead of full fare
                service_type: bookingState.service_type || "point_to_point",
                passenger_first_name: formData.get("full-name")?.split(' ')[0] || '',
                passenger_last_name: formData.get("full-name")?.split(' ').slice(1).join(' ') || '',
                customer_email: formData.get("email"),
                customer_cell_no: phone,
                fleet_name: fleet_selection?.name || "Standard Car",
                fare_details: {
                    final_fare: fleet_selection?.fare_details?.final_fare || fleet_selection?.fare_details?.total_fare || 0,
                    selected_fleet_id: bookingState.fleet_id // ✅ Use Redux fleet_id consistently
                },
                quote_info_record_id: quote_info_record_id,
                maps_record_id: maps_record_id,
                fleet_record_with_rate_id: fleet_record_with_rate_id,
                selected_vehicle_id: selected_vehicle_id,
                booking_info_record_id: booking_info_record_id,
                flight_name: formData.get("flight-name") || "",
                flight_number: formData.get("flight-number") || "",
                special_requirements: "",
                // Pass dynamic data from Redux state
                passengers: bookingState.passenger || 1,
                luggage: bookingState.luggage || 1,
                domain_id: bookingState.domain_id,
                infant_seat: bookingState.infant || 0,
                regular_seat: bookingState.regular || 0,
                booster_seat: bookingState.booster || 0
            };

            const bookingResponse = await createBookingWithPayment(confirmData);
            console.log("Booking creation response:", bookingResponse);

            if (!bookingResponse.booking_ref_id) {
                throw new Error('Booking creation failed - no booking reference received');
            }

            console.log('[Payment] Booking created successfully, now processing payment...');

            // Tokenize the card using Square
            const tokenResult = await squareCard.tokenize();
            
            if (tokenResult.status !== 'OK') {
                const errorMessage = tokenResult.errors?.[0]?.detail || 'Card validation failed';
                throw new Error(errorMessage);
            }

            // Process payment directly
            const paymentData = {
                booking_ref_id: bookingResponse.booking_ref_id,
                amount: "1.00", // ✅ Charge $1 instead of full fare
                card_token: tokenResult.token,
                card_holder_name: cardHolderName.trim(),
                is_save_cc: "0"
            };

            console.log('[Payment] Processing Square payment...');
            const paymentResult = await processSquarePayment(paymentData);

            if (paymentResult.status === 1) {
                console.log('[Payment] Payment successful!');
                
                // Navigate to success page
                navigate('/order-complete', {
                    state: {
                        bookingRefId: bookingResponse.booking_ref_id,
                        paymentResult: paymentResult,
                        transactionId: paymentResult.data?.transaction_id,
                        amount: paymentData.amount,
                        paymentMethod: 'Square'
                    }
                });
            } else {
                throw new Error(paymentResult.msg || 'Payment processing failed');
            }

        } catch (error) {
            console.error("Error in booking/payment process:", error);
            setPaymentError(error.message || 'An error occurred during booking/payment processing');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            { loading && (
                <div className="full-page-loader">
                    <img src={ loader } width="200px" alt="Loading..." />
                </div>
            ) }
            <form onSubmit={handleConfirm}>
                <div className="container-fluid payment-fields p-0">
                    <div className="row">
                        <div className="col-12">
                            <div className="form-group">
                                <div className="field-icon"><FaUser /></div>
                            <input type="text" className="field-style form-control" name="full-name" required placeholder="Name"/>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="form-group">
                            <div className="field-icon"><FaEnvelope /></div>
                            <input type="email" className="field-style form-control" name="email" required placeholder="Email"/>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="form-group">
                            <div className="field-icon"><FaPhoneAlt  /></div>
                            <div className="tel-number-field">
                                {/* Country Code Dropdown */ }
                                <select
                                    value={ countryCode }
                                    onChange={ (e) => setCountryCode(e.target.value) }
                                    className="field-style form-control"
                                >
                                    <option value="+1">+1 (USA)</option>
                                    <option value="+44">+44 (UK)</option>
                                    <option value="+91">+91 (India)</option>
                                    <option value="+61">+61 (Australia)</option>
                                    <option value="+33">+33 (France)</option>
                                    <option value="+49">+49 (Germany)</option>
                                    <option value="+81">+81 (Japan)</option>
                                    <option value="+86">+86 (China)</option>
                                    <option value="+55">+55 (Brazil)</option>
                                    <option value="+7">+7 (Russia)</option>
                                    <option value="+34">+34 (Spain)</option>
                                    <option value="+39">+39 (Italy)</option>
                                    <option value="+27">+27 (South Africa)</option>
                                    {/* Add more country codes as needed */ }
                                </select>
                                {/* Phone Number Input */ }
                                <input
                                    type="tel"
                                    className="field-style form-control"
                                    name="phone"
                                    placeholder="(201) 555-0123"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="form-group">
                            <div className="field-icon"><FaPlaneDeparture  /></div>
                            <input type="text" className="field-style form-control" name="flight-name" placeholder="Flight Name" required/>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="form-group">
                            <div className="field-icon"><FaPlane /></div>
                            <input type="text" className="field-style form-control" name="flight-number" required placeholder="Flight Number"/>
                        </div>
                    </div>
                </div>
                <div className="pt-4">
                    <h3 className="pay-with-card">Payment Information</h3>
                    <h6 className="text-lg font-semibold mb-4">
                        <i>Note :- (We only charge $1 for confirm booking.)</i>
                    </h6>

                    {/* Integrated Square Payment Form */}
                    <div className="payment-section">
                        <div className="">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Card Information
                            </label>
                            <div 
                                id="square-payment-card" 
                                className="min-h-[56px] border-2 border-gray-600 rounded-0 p-3 bg-white"
                                style={{ borderRadius: '0' }}
                            ></div>
                            <p className="text-xs text-gray-500 mt-1">
                                Enter your card number, expiry date, CVC, and Zip code
                            </p>
                        </div>

                        <div className="form-group">
                            <div className="field-icon"><FaCreditCard /></div>
                            <input
                                type="text"
                                className="field-style form-control"
                                placeholder="Cardholder Name"
                                value={cardHolderName}
                                onChange={(e) => setCardHolderName(e.target.value)}
                                required
                            />
                        </div>

                        {/* Payment Error Display */}
                        {paymentError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-0">
                                <p className="text-red-600 text-sm">{paymentError}</p>
                            </div>
                        )}

                        {/* Test Cards for Sandbox */}
                        {/* <div className="bg-blue-50 border border-blue-200 rounded-0 p-3 mb-4">
                            <h5 className="text-sm font-medium text-blue-800 mb-2">Test Cards (Sandbox)</h5>
                            <div className="text-xs text-blue-700 space-y-1">
                                <p><strong>Visa:</strong> 4111 1111 1111 1111</p>
                                <p><strong>Mastercard:</strong> 5555 5555 5555 4444</p>
                                <p><strong>Declined:</strong> 4000 0000 0000 0002</p>
                                <p className="text-blue-600 mt-2">Use any future expiry date and any 3-4 digit CVC</p>
                            </div>
                        </div> */}
                    </div>
                </div>
                <button
                    type="submit"
                    className="confirm-order"
                    disabled={loading}
                >
                    {loading ? 'PROCESSING...' : 'CONFIRM & PAY'}
                </button>
            </div>
        </form>
        </>
    );
}

export default Payment;
